'use client'

import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Check, MessageSquareText, X } from 'lucide-react'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp, Trail } from './demoParts'

// A2P 10DLC: the same registration filed two ways, using the page's three
// common mistakes (EIN format, business details that don't match public
// records, a campaign description that doesn't match the real messages).
// The DIY one is rejected and texts get filtered; GHL Prime's is approved
// first time and the delivery meter fills. DIY plays first, then GHL Prime.

const PATHS = {
  diy: {
    fields: [
      { label: 'EIN', value: '123456789', note: 'Wrong format', ok: false },
      { label: 'Legal business name', value: 'Bright Homes', note: 'Does not match public records', ok: false },
      { label: 'Campaign description', value: 'Marketing messages', note: 'Does not match what you send', ok: false },
    ],
    trail: [
      { at: 4, text: 'Brand submitted to the Campaign Registry' },
      { at: 5, text: 'Brand rejected: details do not match', tone: 'bad' },
      { at: 6, text: 'Campaign rejected: vague description', tone: 'bad' },
      { at: 7, text: 'Carriers silently filter your texts', tone: 'bad' },
    ],
    meter: { fill: 6, label: 'Most texts filtered before they arrive' },
    result: { tone: 'bad', text: 'Rejected · texts filtered' },
  },
  prime: {
    fields: [
      { label: 'EIN', value: '12-3456789', note: 'Formatted correctly', ok: true },
      { label: 'Legal business name', value: 'Bright Home Services LLC', note: 'Matches public records', ok: true },
      { label: 'Campaign description', value: 'Appointment reminders and service updates for customers who opted in', note: 'Matches the real messages', ok: true },
    ],
    trail: [
      { at: 4, text: 'Brand submitted to the Campaign Registry' },
      { at: 5, text: 'Brand verified' },
      { at: 6, text: 'Campaign approved on first submission' },
      { at: 7, text: 'Numbers linked, texts landing' },
    ],
    meter: { fill: 97, label: 'Texts reaching your contacts' },
    result: { tone: 'good', text: 'Approved first time' },
  },
}
const DELAYS = [500, 900, 900, 900, 1000, 1000, 1000, 800]

export default function A2pCheck() {
  const [ref, onScreen] = useOnScreen()
  const [path, setPath] = useState('diy')
  const [run, setRun] = useState(0)
  const [auto, setAuto] = useState(true)
  const data = PATHS[path]
  const step = useSequence(DELAYS, { run: onScreen, resetKey: `${path}-${run}` })
  const finished = step >= DELAYS.length

  useEffect(() => {
    if (!auto || path !== 'diy' || !finished) return undefined
    const timer = setTimeout(() => setPath('prime'), 1800)
    return () => clearTimeout(timer)
  }, [auto, path, finished])

  const pick = (key) => { setAuto(false); setPath(key); setRun((n) => n + 1) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-a2p"
      title="One registration, two outcomes"
      label="Demo: an A2P 10DLC registration filed with common mistakes versus filed correctly by GHL Prime"
      tabs={[{ key: 'diy', label: 'Filed yourself', tone: 'bad' }, { key: 'prime', label: 'Filed by GHL Prime', tone: 'good' }]}
      activeTab={path}
      onTab={pick}
      onReplay={() => pick(path)}
    >
      <div className="svd-agent-grid">
        <div className="svd-form-card">
          <span className="svd-form-title">A2P brand + campaign</span>
          {data.fields.map((f, i) => {
            const shown = step > i
            return (
              <div key={`${path}-${f.label}`} className={`svd-form-field${shown ? (f.ok ? ' is-ok' : ' is-bad') : ''}`}>
                <span className="svd-form-label">{f.label}</span>
                <span className="svd-form-value">{shown ? f.value : <span className="svd-skeleton" />}</span>
                {shown ? (
                  <motion.span className="svd-form-note" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>
                    {f.ok ? <Check size={12} strokeWidth={3} aria-hidden="true" /> : <X size={12} strokeWidth={3} aria-hidden="true" />} {f.note}
                  </motion.span>
                ) : null}
              </div>
            )
          })}
        </div>
        <Trail title="Registration status" items={data.trail} step={step} />
      </div>

      <div className={`svd-meter is-${data.result.tone}`} aria-label={data.meter.label}>
        <span className="svd-meter-head"><MessageSquareText size={14} aria-hidden="true" /> Message delivery</span>
        <span className="svd-meter-track"><motion.i key={`${path}-${run}`} initial={{ width: 0 }} animate={{ width: step >= 7 ? `${data.meter.fill}%` : '0%' }} transition={{ duration: 0.9, ease: 'easeOut' }} /></span>
        <small>{step >= 7 ? data.meter.label : 'Waiting for carrier approval…'}</small>
      </div>
      <ResultStamp show={finished} tone={data.result.tone} text={data.result.text} id={`${path}-${run}`} />
    </DemoFrame>
  )
}
